type TimestampLike = {
  seconds?: number;
  nanoseconds?: number;
  toDate?: () => Date;
};

/** Plain `{ seconds, nanoseconds }` or Firebase `Timestamp` class instance. */
export function isFirestoreTimestampLike(value: unknown): value is TimestampLike {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const candidate = value as TimestampLike;
  if (typeof candidate.toDate === 'function') {
    return true;
  }
  return (
    'seconds' in candidate &&
    'nanoseconds' in candidate &&
    typeof candidate.seconds === 'number' &&
    typeof candidate.nanoseconds === 'number'
  );
}

function timestampLikeToIso(value: TimestampLike): string {
  if (typeof value.toDate === 'function') {
    return value.toDate().toISOString();
  }
  const ms = (value.seconds ?? 0) * 1000 + (value.nanoseconds ?? 0) / 1e6;
  return new Date(ms).toISOString();
}

/** True if the value tree contains any Firestore Timestamp (e.g. `lastLoginAt`). */
export function containsFirestoreTimestampLike(value: unknown): boolean {
  if (isFirestoreTimestampLike(value)) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.some(containsFirestoreTimestampLike);
  }
  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    return Object.values(value as Record<string, unknown>).some(containsFirestoreTimestampLike);
  }
  return false;
}

/**
 * Recursively convert Firestore Timestamps to ISO date strings so Redux
 * store stays serializable (avoids "non-serializable value" warnings).
 */
export function serializeForRedux<T>(value: T): T {
  if (isFirestoreTimestampLike(value)) {
    return timestampLikeToIso(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map(serializeForRedux) as T;
  }
  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
      out[key] = serializeForRedux(obj[key]);
    }
    return out as T;
  }
  return value;
}
